import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { forkJoin } from 'rxjs';
import { DashboardService } from '../common/services/dashboard.service';
import { DashboardSummary } from '../common/types/dashboard';
import { initMonths, initYears, onOrganisationChange } from '../common/utils';
import { TranslateService } from '@ngx-translate/core';
import {  colors, DASHBOARD_FILTER_DATA, dashboardChartConfig, toastrConfig } from '../app.config';
import { CommonService } from '../common/services/common.service';
import { ToastrService } from 'ngx-toastr';
import { FilterData, IdentificationData, OwnOrganisationsAndSites } from '../common/types/common';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit {
  isLoading: boolean;
  stateOptions: any[] = [{ label: 'Visitors', value: 'visitors' },{ label: 'Visits', value: 'visits' }];
  stateValue: string = 'visitors';
  selectedDate: string;
  lastYearDate: string;
  currentYearData: any = {};
  lastYearData: any = {};
  visitorAnalytics: any;
  years: number[] = [];
  months: any[];
  selectedYear: number;
  selectedMonth: number;

  selectedOrganisation: any = {id: null};
  selectedSite: any = {id: null};
  ownOrganisationsAndSites: OwnOrganisationsAndSites[];
  organisations: IdentificationData[];
  sites: any[];
  savedFilterData: FilterData = null;

  dashboardSummary: DashboardSummary = {
    totalVisitor: 0,
    totalVisitorDiff: 0,
    avgOccupancy: 0,
    avgOccupancyDiff: 0,
    busiestHour: '',
    lastBusiestHour: '',
    mostVisitedHour: '',
    lastMostVisitedHour: '',
    mostVisitedDay: '',
    lastMostVisitedDay: ''
  };

  charts: any[] = [];

  constructor(private dashboardService: DashboardService,
              private commonService: CommonService,
              private translateService: TranslateService,
              private toastr: ToastrService) { }

  ngOnInit() {
    this.isLoading = true;
    this.years = initYears();
    this.months = initMonths();
    const currentDate = new Date();
    this.savedFilterData = JSON.parse(localStorage.getItem(DASHBOARD_FILTER_DATA));

    this.commonService.getOwnOrganisationsAndSites().subscribe({
      next: (data) => {
        this.ownOrganisationsAndSites = data;
        this.organisations = this.ownOrganisationsAndSites.map(item => item.organisation);
        if (this.savedFilterData) {
          this.selectedYear = this.savedFilterData.selectedYear;
          this.selectedMonth = this.savedFilterData.selectedMonth;
          this.selectedOrganisation = this.organisations.find(
            org => org.id === this.savedFilterData.selectedOrganisation?.id
          ) || this.organisations[0];
          this.onOrganisationChange(this.selectedOrganisation);
          this.selectedSite = this.savedFilterData.selectedSite;
        } else {
          this.selectedYear = currentDate.getFullYear();
          this.selectedMonth = currentDate.getMonth() + 1;
          this.selectedOrganisation = this.organisations[0];
          this.onOrganisationChange(this.selectedOrganisation);
        }
        this.refreshDashboard();
      },
      error: () => {
        this.toastr.error(this.translateService.instant("ADMIN.COMMON_OPTIONS.GET_DATA_ERROR"), "", toastrConfig)
      }
    });
  }

  compareObjects(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  onOrganisationChange(selectedOrganisation: any) {
    this.selectedOrganisation = selectedOrganisation;
    this.sites = onOrganisationChange(this.ownOrganisationsAndSites, selectedOrganisation.id);
    this.selectedSite.id = 'ALL';
  }

  onStateChange(newValue: any) {
    this.stateValue = newValue;
    this.isLoading = true;
    this.refreshDashboard();
  }

  refreshDashboard() {
    this.selectedDate = `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-01`;
    this.lastYearDate = `${this.selectedYear - 1}-${String(this.selectedMonth).padStart(2, '0')}-01`;
    this.savedFilterData = {
      selectedYear: this.selectedYear,
      selectedMonth: this.selectedMonth,
      selectedOrganisation: this.selectedOrganisation,
      selectedSite: this.selectedSite
    }
    localStorage.setItem(DASHBOARD_FILTER_DATA, JSON.stringify(this.savedFilterData));
    this.charts = [];

    const getMetrics = (date: string, organisation: string, site: string) => {
      const requests: any = {
        hourlyAvgInsiders: this.dashboardService.getSiteHourlyAvgInsidersForMonth(date, organisation, site),
        hourlyAvgVisitors: this.dashboardService.getSiteHourlyAvgVisitorsForMonth(date, organisation, site),
        visitorsPerMonth: this.dashboardService.getSiteVisitorsPerMonth(date, organisation, site),
        visitorsPerWeek: this.dashboardService.getSiteVisitorsPerWeek(date, organisation, site),
        visitorsPerDay: this.dashboardService.getSiteVisitorsPerDay(date, organisation, site)
      };
      if (this.selectedSite.id !== 'ALL') {
        requests.visitorAnalytics = this.dashboardService.getVisitorAnalytics(date, organisation, site);
      }
      return forkJoin(requests);
    }

    forkJoin([getMetrics(this.selectedDate, this.selectedOrganisation.id, this.selectedSite.id),
              getMetrics(this.lastYearDate, this.selectedOrganisation.id, this.selectedSite.id)]).subscribe({
      next: ([current, last]) => {
        this.currentYearData = current;
        this.lastYearData = last;
        this.charts = [];

        this.calculateSummary();
        this.getAverageOccupancyInADayChartConfig();
        this.getAverageVisitorsInADayChartConfig();
        this.getTotalVisitorsPerMonthChartConfig();
        this.getTotalVisitorsPerWeekChartConfig();
        this.getTotalVisitorsPerDayChartConfig();
        if (this.selectedSite.id !== 'ALL') {
          this.visitorAnalytics = this.stateValue === 'visitors' ? this.currentYearData.visitorAnalytics.visitors : this.currentYearData.visitorAnalytics.visits;
          this.getAgeAndGenderDistributionChartConfig();
          this.getNewVsReturningVisitorsChartConfig();
          this.getGenderDistributionChartConfig();
        } else {
          this.visitorAnalytics = null;
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error(this.translateService.instant("ADMIN.COMMON_OPTIONS.GET_DATA_ERROR"), "", toastrConfig);
        this.isLoading = false;
      }
    });
  }

  calculateSummary() {
    let maxValue: any;

    //totalVisitor, totalVisitorDiff
    this.dashboardSummary.totalVisitor = this.currentYearData.visitorsPerDay.reduce((sum, day) => sum + day.visitors, 0);
    const lastYearTotalVisitor = this.lastYearData.visitorsPerDay.reduce((sum, day) => sum + day.visitors, 0);
    this.dashboardSummary.totalVisitorDiff = this.dashboardSummary.totalVisitor - lastYearTotalVisitor;

    // avgOccupancy, avgOccupancyDiff
    this.dashboardSummary.avgOccupancy = this.currentYearData.hourlyAvgInsiders.reduce((sum, day) => sum + day.value, 0) / this.currentYearData.hourlyAvgInsiders.length;
    const lastYearAvgOccupancy= this.lastYearData.hourlyAvgInsiders.reduce((sum, day) => sum + day.value, 0) / this.lastYearData.hourlyAvgInsiders.length;
    this.dashboardSummary.avgOccupancyDiff = this.dashboardSummary.avgOccupancy - lastYearAvgOccupancy;

    // busiestHour, lastBusiestHour
    maxValue = this.currentYearData.hourlyAvgInsiders.reduce((max, item) =>
      (item.value > max.value) ? item : max,
      this.currentYearData.hourlyAvgInsiders[0]);
    this.dashboardSummary.busiestHour = maxValue.hour.toString().padStart(2, '0') + ":00-" + (maxValue.hour + 1).toString().padStart(2, '0') + ":00";
    maxValue = this.lastYearData.hourlyAvgInsiders.reduce((max, item) =>
        (item.value > max.value) ? item : max,
      this.lastYearData.hourlyAvgInsiders[0]);
    this.dashboardSummary.lastBusiestHour = maxValue.hour.toString().padStart(2, '0') + ":00-" + (maxValue.hour + 1).toString().padStart(2, '0') + ":00";

    // mostVisitedHour, lastMostVisitedHour
    maxValue = this.currentYearData.hourlyAvgVisitors.reduce((max, item) =>
        (item.value > max.value) ? item : max,
      this.currentYearData.hourlyAvgVisitors[0]);
    this.dashboardSummary.mostVisitedHour = maxValue.hour.toString().padStart(2, '0') + ":00-" + (maxValue.hour + 1).toString().padStart(2, '0') + ":00";
    maxValue = this.lastYearData.hourlyAvgVisitors.reduce((max, item) =>
        (item.value > max.value) ? item : max,
      this.lastYearData.hourlyAvgVisitors[0]);
    this.dashboardSummary.lastMostVisitedHour = maxValue.hour.toString().padStart(2, '0') + ":00-" + (maxValue.hour + 1).toString().padStart(2, '0') + ":00";

    // mostVisitedDay, lastMostVisitedDay
    maxValue = this.currentYearData.visitorsPerDay.reduce((max, item) =>
      (item.visitors > max.visitors) ? item : max,
      this.currentYearData.visitorsPerDay[0]);
    this.dashboardSummary.mostVisitedDay = maxValue.day;
    maxValue = this.lastYearData.visitorsPerDay.reduce((max, item) =>
        (item.visitors > max.visitors) ? item : max,
        this.lastYearData.visitorsPerDay[0]);
    this.dashboardSummary.lastMostVisitedDay = maxValue.day;
  }

  getAgeAndGenderDistributionChartConfig() {
    const chartData = this.commonService.getAgeAndGenderDistributionChartConfig(this.visitorAnalytics.ageGenderDistribution);
    this.charts.push({ type: chartData.type, data: chartData.data, options: chartData.options });
  }

  getNewVsReturningVisitorsChartConfig() {
    const chartData = this.commonService.getNewVsReturningVisitorsChartConfig(this.visitorAnalytics.newVsReturning);
    this.charts.push({ data: chartData.data, legendData: chartData.legendData });
  }

  getGenderDistributionChartConfig() {
    const chartData = this.commonService.getGenderDistributionChartConfig(this.visitorAnalytics.genderDistribution);
    this.charts.push({ type: chartData.type, data: chartData.data, options: chartData.options, legendData: chartData.legendData, totalVisitors: chartData.totalVisitors });
  }

  getAverageOccupancyInADayChartConfig() {
    const hours = this.currentYearData.hourlyAvgInsiders.map(data => data.hour + ":00");
    const avgInsiders = this.currentYearData.hourlyAvgInsiders.map(data => data.value < 0 ? 0 : data.value);
    const lastYearAvgInsiders = this.lastYearData.hourlyAvgInsiders.map(data => data.value < 0 ? 0 : data.value);
    const max = Math.max.apply(null, avgInsiders);
    const backgroundColors = avgInsiders.map(value => value === max ? colors.secondary : colors.primary);

    const data = {
      labels: hours,
      datasets: [
        {
          label: this.translateService.instant("DASHBOARD.OCCUPANCY"),
          data: avgInsiders,
          backgroundColor: backgroundColors,
          fill: true
        },
        {
          label: this.translateService.instant("DASHBOARD.LAST_YEAR"),
          data: lastYearAvgInsiders,
          type: 'line',
          fill: false,
          borderColor: colors.blue
        }
      ]
    };

    this.charts.push({ type: 'bar', data: data });
  }

  getAverageVisitorsInADayChartConfig() {
    const hours = this.currentYearData.hourlyAvgVisitors.map(data => data.hour + ":00");
    const avgVisitors = this.currentYearData.hourlyAvgVisitors.map(data => data.value < 0 ? 0 : data.value);
    const lastYearAvgVisitors = this.lastYearData.hourlyAvgVisitors.map(data => data.value < 0 ? 0 : data.value);
    const max = Math.max.apply(null, avgVisitors);
    const backgroundColors = avgVisitors.map(value => value === max ? colors.secondary : colors.primary);

    const data = {
      labels: hours,
      datasets: [
        {
          label: this.translateService.instant("DASHBOARD.VISITORS"),
          data: avgVisitors,
          backgroundColor: backgroundColors,
          fill: true
        },
        {
          label: this.translateService.instant("DASHBOARD.LAST_YEAR"),
          data: lastYearAvgVisitors,
          type: 'line',
          fill: false,
          borderColor: colors.blue
        }
      ]
    };

    this.charts.push({ type: 'bar', data: data });
  }

  getTotalVisitorsPerMonthChartConfig() {
    const months = this.currentYearData.visitorsPerMonth.map(data => data.month);
    const visitorsPerMonth = this.currentYearData.visitorsPerMonth.map(data => data.visitors < 0 ? 0 : data.visitors);
    const lastYearVisitorsPerMonth = this.lastYearData.visitorsPerMonth.map(data => data.visitors < 0 ? 0 : data.visitors);
    const max = Math.max.apply(null, visitorsPerMonth);
    const backgroundColors = visitorsPerMonth.map(visitors => visitors === max ? colors.secondary : colors.primary);

    const data = {
      labels: months,
      datasets: [
        {
          label: this.translateService.instant("DASHBOARD.VISITORS"),
          data: visitorsPerMonth,
          backgroundColor: backgroundColors,
          fill: true
        },
        {
          label: this.translateService.instant("DASHBOARD.LAST_YEAR"),
          data: lastYearVisitorsPerMonth,
          type: 'line',
          fill: false,
          borderColor: colors.blue
        }
      ]
    };

    this.charts.push({ type: 'bar', data: data });
  }

  getTotalVisitorsPerWeekChartConfig() {
    const weeks = this.currentYearData.visitorsPerWeek.map(data => data.week);
    const visitorsPerWeeks = this.currentYearData.visitorsPerWeek.map(data => data.visitors < 0 ? 0 : data.visitors);
    const lastYearVisitorsPerWeeks = this.lastYearData.visitorsPerWeek.map(data => data.visitors < 0 ? 0 : data.visitors);
    const max = Math.max.apply(null, visitorsPerWeeks);
    const backgroundColors = visitorsPerWeeks.map(visitors => visitors === max ? colors.secondary : colors.primary);

    const data = {
      labels: weeks,
      datasets: [
        {
          label: this.translateService.instant("DASHBOARD.VISITORS"),
          data: visitorsPerWeeks,
          backgroundColor: backgroundColors,
          fill: true
        },
        {
          label: this.translateService.instant("DASHBOARD.LAST_YEAR"),
          data: lastYearVisitorsPerWeeks,
          type: 'line',
          fill: false,
          borderColor: colors.blue
        }
      ]
    };

    this.charts.push({ type: 'bar', data: data });
  }

  getTotalVisitorsPerDayChartConfig() {
    const days = this.currentYearData.visitorsPerDay.map(data => data.day);
    const visitorsPerDays = this.currentYearData.visitorsPerDay.map(data => data.visitors < 0 ? 0 : data.visitors);
    const lastYearVisitorsPerDays = this.lastYearData.visitorsPerDay.map(data => data.visitors < 0 ? 0 : data.visitors);
    const max = Math.max.apply(null, visitorsPerDays);
    const backgroundColors = visitorsPerDays.map(visitors => visitors === max ? colors.secondary : colors.primary);

    const data = {
      labels: days,
      datasets: [
        {
          label: this.translateService.instant("DASHBOARD.VISITORS"),
          data: visitorsPerDays,
          backgroundColor: backgroundColors,
          fill: true
        },
        {
          label: this.translateService.instant("DASHBOARD.LAST_YEAR"),
          data: lastYearVisitorsPerDays,
          type: 'line',
          fill: false,
          borderColor: colors.blue
        }
      ]
    };

    this.charts.push({ type: 'bar', data: data });
  }

  protected readonly dashboardChartConfig = dashboardChartConfig;
  protected readonly colors = colors;
}
